<script lang="ts">
    // Types
    import type { Artwork as ArtworkObj } from '@amp/web-app-components/src/components/Artwork/types';

    // Shared Components
    import Artwork from '@amp/web-app-components/src/components/Artwork/Artwork.svelte';

    // Utils
    import { sanitizeHtml } from '@amp/web-app-components/src/utils/sanitize-html';

    export let title: string;
    export let headline: string = null;
    export let rankingText: string = null;
    export let subtitle: string = null;
    export let tertiaryText: string = null;
    export let hasHeader: boolean = false;
    export let isPlayable: boolean = false;

    export let artwork: ArtworkObj = null;
    export let artworkProfile: string = null;
    export let artworkAlt: string = null;
</script>

<div
    class="shelf-list-item"
    class:shelf-list-item--has-header={hasHeader}
    data-testid="shelf-list-item"
>
    {#if $$slots['primary-action']}
        <div class="shelf-list-item__action">
            <slot name="primary-action" />
        </div>
    {/if}

    {#if hasHeader && $$slots['header']}
        <div
            class="shelf-list-item__header"
            data-testid="shelf-list-item-header"
        >
            <slot name="header" />
        </div>
    {/if}

    {#if artworkProfile}
        <div
            class="shelf-list-item__artwork-wrapper"
            class:shelf-list-item__artwork-wrapper--ellipse={artworkProfile ===
                'ellipse-lockup'}
            data-testid="shelf-list-item-artwork"
        >
            {#if isPlayable && $$slots['play-button']}
                <div
                    class="shelf-list-item__play-button-wrapper"
                    data-testid="shelf-list-item-play-button"
                >
                    <slot name="play-button" />
                </div>
            {/if}

            <Artwork
                {artwork}
                profile={artworkProfile}
                alt={artworkAlt || title}
            >
                <svelte:fragment slot="placeholder-component">
                    {#if $$slots['fallback']}
                        <div
                            class="artwork-fallback"
                            data-testid="artwork-fallback"
                        >
                            <slot name="fallback" />
                        </div>
                    {/if}
                </svelte:fragment>
            </Artwork>
        </div>
    {/if}

    {#if rankingText}
        <div class="lockup-ranking">
            {rankingText}
        </div>
    {/if}

    <ul class="shelf-list-item__content">
        {#if headline}
            <li
                class="shelf-list-item__headline"
                data-testid="shelf-list-item-headline"
            >
                {@html sanitizeHtml(headline)}
            </li>
        {/if}

        <li class="shelf-list-item__title" data-testid="shelf-list-item-title">
            <slot name="title">
                <div class="shelf-list-item__clamp-wrapper">
                    {title}
                </div>
            </slot>
        </li>

        <li
            class="shelf-list-item__subtitle"
            data-testid="shelf-list-item-subtitle"
        >
            <slot name="subtitle">
                <div class="shelf-list-item__clamp-wrapper">
                    {subtitle || ''}
                </div>
            </slot>
        </li>
        {#if !headline && tertiaryText}
            <span class="shelf-list-item__tertiary-title">
                {@html sanitizeHtml(tertiaryText, {
                    allowedTags: ['mark'],
                })}
            </span>
        {/if}
    </ul>

    <div class="shelf-list-item__secondary-actions">
        <slot name="secondary-actions" />
    </div>
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/locale' as *;
    @use 'ac-sasskit/core/helpers' as *;
    @use 'amp/stylekit/core/mixins/browser-targets' as *;
    @use 'amp/stylekit/core/mixins/line-clamp' as *;
    @use 'amp/stylekit/core/mixins/overflow-bleed' as *;
    @use '@amp/web-shared-styles/app/core/globalvars' as *;
    @use '@amp/web-shared-styles/app/core/mixins/aspect-ratio' as *;
    @use '@amp/web-shared-styles/app/core/mixins/focus' as *;
    @use '@amp/web-shared-styles/app/core/mixins/transgray-scrim' as *;
    @use '@amp/web-shared-styles/app/core/mixins/absolute-center' as *;

    .shelf-list-item {
        padding-top: 7.5px;
        padding-bottom: 7.5px;
        width: 100%;
        display: grid;
        grid-template-areas:
            'header header header header'
            'artwork ranking content actions';
        grid-template-rows: auto;
        grid-template-columns: auto auto 1fr auto;
        position: relative;
        color: var(--systemPrimary);
        padding-inline-end: var(--trackLockupPaddingInlineEnd, 14px);

        &::after {
            content: '';
            position: absolute;
            top: 0;
            z-index: var(--z-default);
            inset-inline-start: 0;
            inset-inline-end: 0;
            border-top: var(
                --shelf-list-item-keyline-border,
                var(--keyline-border-style)
            );
        }

        :global(.play-button) {
            opacity: 0;
        }

        // Target the "inactive" track lockup, so the add button collapses in this scenario and returns to its regular width otherwise.
        &:not(.is-playing, .is-paused, :hover, :focus-within) {
            :global(.add-to-library-button--icon-only) {
                // Collapse add button entirely when it's not shown, so long titles can fill the extra space.
                // We can't use `display: none` here, because it needs to remain in the accessibility tree.
                width: 0;
            }
        }

        &.is-playing,
        &.is-paused:not(.is-suggestion),
        &:hover,
        &:focus-within {
            .shelf-list-item__play-button-wrapper {
                --transgray-scrim-z: calc(var(--z-default) + 1);
                border-radius: inherit;

                @include transgray-scrim(tracklist);
            }

            :global(.play-button) {
                opacity: 1;
            }
        }
    }

    .shelf-list-item__play-button-wrapper {
        @include absolute-center;

        // Remove focus outline inset in artwork
        :global(:focus-visible) {
            box-shadow: none;
        }

        // Add focus outline around artwork (entire button)
        &:has(:focus-visible) {
            @include focus-outline;
        }

        &:hover {
            cursor: pointer;
        }
    }

    .shelf-list-item__content,
    .shelf-list-item__secondary-actions {
        // Place lockup content on z layer above primary action
        position: relative;
        z-index: calc(var(--z-default) + 1);

        // Forward lockup pointer events to primary action
        pointer-events: none;

        // Do *not* forward pointer events for any interactive elements nested inside
        :global(a),
        :global(button) {
            pointer-events: all;
        }
    }

    .shelf-list-item__content {
        align-self: stretch; // Container fills grid area
        display: flex;
        flex-flow: column;
        justify-content: center; // Vertically center children within grid area
        align-items: flex-start;
        overflow: hidden;
        text-align: start;
        flex: 1;
        grid-area: content;

        // Use padding instead of margin to prevent clipping of focus rings
        // before titles and subtitles
        padding-inline-start: 4px;
        padding-inline-end: 4px;
        margin-inline-end: 8px;

        @include target-safari {
            // Fix for rdar://97761387 (CSS Grid fails within a flex-box when resizing window)
            flex-grow: 1;
        }
    }

    .shelf-list-item__title {
        --explicitBadgeSize: 10px;
        display: inline-grid;
        align-items: baseline;

        // Second and third column (favorite and explicit badges) collapse when not present
        grid-template-columns: 1fr auto auto;
        font: var(--body-tall);

        @include rtl {
            // Lock order of text and explicit icon to RTL direction (explicit icon on left)
            // Note: `dir="auto"` in the HTML ensures our text truncates on the correct end of string
            direction: rtl;
        }

        :global(.favorite) {
            // Inline glyph padding for favorite badge
            padding-inline-start: 2px;
            padding-inline-end: 2px;
        }

        :global(.explicit) {
            margin-inline-start: 4px;
        }
    }

    .shelf-list-item__subtitle {
        --linkHoverColor: inherit;
        --linkHoverTextDecoration: underline;
        max-width: 100%; // Ensure subtitles are truncated with ellipsis
        color: var(--systemSecondary);
        font: var(--callout);
    }

    .shelf-list-item__tertiary-title {
        color: var(--systemSecondary);
        font: var(--body-tall);

        @include line-clamp(1);

        :global(mark) {
            color: var(--systemPrimary);
            background-color: unset;
        }
    }

    // Applies line-clamp to contents (title, subtitles) without
    // clipping focus rings
    .shelf-list-item__clamp-wrapper {
        @include line-clamp;
        @include overflow-bleed($focus-size);
    }

    .shelf-list-item__headline {
        --linkHoverColor: inherit;
        --linkHoverTextDecoration: underline;
        max-width: 100%; // Ensure subtitles are truncated with ellipsis
        color: var(--systemSecondary);
        font: var(--caption-2-emphasized);
    }

    .shelf-list-item__artwork-wrapper {
        position: relative;
        border-radius: var(
            --global-border-radius-small,
            #{$global-border-radius-small}
        );
        margin-inline-end: 12px;
        background-color: var(--genericJoeColor);
        grid-area: artwork;
        margin-top: 6px;
        margin-bottom: 8px;
        width: var(--artwork-override-width);
        height: var(--artwork-override-height);

        &--ellipse {
            &,
            > :global(.artwork-component) {
                border-radius: 50%;
            }
        }
    }

    .shelf-list-item__header {
        grid-area: header;
        font: var(--callout-emphasized);
        height: 29px;
        padding-top: 12px;
        padding-bottom: 2px;
        position: relative;
        z-index: calc(var(--z-default) + 1);
    }

    .shelf-list-item__secondary-actions {
        grid-area: actions;
        display: flex;
        align-items: center;
    }

    .shelf-list-item__action {
        @include absolute-center;

        // Cover entire lockup with segue or play button
        :global(a),
        :global(button) {
            border-radius: inherit;
            display: block;
            width: 100%;
            height: 100%;
        }
    }
</style>
